import React, { useState, useEffect } from "react";
import UserNotFound from "../../assets/icons/svgicons/UserNotFound.svg";
import { IProject } from "../../@types";
import SelectedMarkIcon from "../../assets/icons/SelectedIcon";
import { useProjectStore } from "../../state";
import { useProjectSearch } from "../../hooks/project/useProjectSearch";
import AddModal from "../Invoice/sections/modal/AddModal";
import { CloseIcon } from "../../assets/icons";
import { NewSearchInput } from "../common/SearchInput/NewSearch";

interface SelectCustomerProps {
  setIsOpen: (isOpen: boolean) => void;
  project?: IProject | null;
  handleSetProject: (selectedProject: IProject | null) => void;
  projectCustomerId: string;
  handleAddProject: () => void;
  isOpenSelectProject: boolean;
}

const SelectProject: React.FC<SelectCustomerProps> = ({
  setIsOpen,
  handleSetProject,
  projectCustomerId,
  handleAddProject,
  isOpenSelectProject,
}) => {
  if (!isOpenSelectProject) return null;

  const {
    data: projects,
    isSearching,
    handleSearch: handleProjectSearch,
  } = useProjectSearch();
  const [isTyping, setIsTyping] = useState(false);
  const [canDisplayError, setCanDisplayError] = useState(false);
  const [displayedProjects, setDisplayedProjects] = useState<IProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const { fetchData, isLoading, projects: data } = useProjectStore();

  useEffect(() => {
    if (projectCustomerId && data && data.length > 0) {
      setDisplayedProjects(
        data
          ?.filter((project) => project.customerId == projectCustomerId)
          ?.slice(0, 3),
      );
    } else if (data && data.length > 0) {
      setDisplayedProjects(data?.slice(0, 3));
    }
  }, [data]);

  const handleSearchChange = (searchText: string) => {
    setCanDisplayError(false);
    setIsTyping(true);
    (async () => {
      try {
        handleProjectSearch(searchText);
        if (searchText.trim() === "") {
          setDisplayedProjects(projects.slice(0, 3));
          setIsTyping(false);
        } else {
          const filteredCustomers = projects.filter((project) =>
            `${project.firstName} ${project.lastName}`
              .toLowerCase()
              .includes(searchText.toLowerCase()),
          );
          setDisplayedProjects(filteredCustomers);
          setIsTyping(false);
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      } catch (error) {
      } finally {
        setCanDisplayError(true);
      }
    })();
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderCustomerItem = (project: IProject) => (
    <div
      key={project.id}
      className={`mt-2 flex justify-between items-center p-2 rounded-md cursor-pointer ${
        selectedProject?.id === project.id
          ? "bg-blue-100 border-2 border-blue-500"
          : "hover:bg-gray-100"
      }`}
      onClick={() => {
        handleSetProject(project);
        setIsOpen(false);
        setSelectedProject(project);
      }}
    >
      <div>
        <h5 className="font-medium text-[14px]">{project.name}</h5>
        {project.customer && (
          <p className="text-gray-600 text-[14px]">{project.customer}</p>
        )}
      </div>
      {selectedProject?.id === project.id ? (
        <SelectedMarkIcon />
      ) : (
        <div className="rounded-full w-4 h-4 border border-gray-600" />
      )}
    </div>
  );

  return (
    <AddModal>
      <div className="flex justify-between items-center my-4">
        <p className="text-[18px] font-semibold leading-6">Select Project</p>
        <div onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </div>
      </div>

      <NewSearchInput
        placeholder="Search project"
        data={displayedProjects}
        value={
          projectCustomerId
            ? projects.find(
                (cust: { id: string | undefined }) =>
                  cust.id === projectCustomerId,
              )?.firstName +
                " " +
                projects.find(
                  (cust: { id: string | undefined }) =>
                    cust.id === projectCustomerId,
                )?.lastName || ""
            : ""
        }
        selected={selectedProject}
        onSelect={(selected) => setSelectedProject(selected as IProject)}
        displayKey="name"
        classNames="w-full border border-gray-300 rounded-lg focus:outline-none"
        searchKey="name"
        handleChangeText={handleSearchChange}
        icon={<></>}
        renderItem={renderCustomerItem}
      />
      {isSearching || isTyping || isLoading ? (
        <div className="mt-5 space-y-4 mx-auto flex w-full">
          <div className="inset-y-0 right-6 flex items-center mx-auto w-full justify-center">
            <div className="w-7 h-7 border-4 border-gray-200 border-t-[#044E97] rounded-full animate-spin"></div>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-4 mx-auto">
          {displayedProjects.length > 0 ? (
            displayedProjects.map(renderCustomerItem)
          ) : (
            <>
              {data && data.length > 0 && displayedProjects.length > 0 ? (
                <div className="mt-5 space-y-4 mx-auto flex w-full">
                  <div className="inset-y-0 right-6 flex items-center mx-auto w-full justify-center">
                    <div className="w-7 h-7 border-4 border-gray-200 border-t-[#044E97] rounded-full animate-spin"></div>
                  </div>
                </div>
              ) : canDisplayError ? (
                <p>No project found</p>
              ) : (
                <div className="mt-5 space-y-4 mx-auto flex flex-col justify-center items-center w-full">
                  <img src={UserNotFound} alt="" />
                  <p className="text-center">No project available</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex justify-between gap-4">
        <button
          type="button"
          onClick={handleAddProject}
          className="w-full py-[10px] text-[#044E97] border border-[#044E97] rounded-[4px] mt-8 font-semibold"
        >
          Add new project
        </button>
      </div>
    </AddModal>
  );
};

export default SelectProject;
